import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  createCustomerPortalCommunicationReply,
  getCustomerPortalCommunicationThread,
  getCustomerPortalCommunications,
  toPortalErrorMessage,
} from "../../services/customerPortalApi";

const DEFAULT_LIMIT = 25;

export default function PortalCommunicationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState({ thread: null, messages: [] });
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replySaving, setReplySaving] = useState(false);
  const [success, setSuccess] = useState("");
  const limit = DEFAULT_LIMIT;
  const selectedIdRef = useRef("");
  const requestedThreadId = String(searchParams.get("thread") || "").trim();

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        const res = await getCustomerPortalCommunications({
          page,
          limit,
          q: String(query || "").trim() || undefined,
          status: statusFilter || undefined,
        });

        if (!active) return;

        const nextItems = Array.isArray(res?.items) ? res.items : [];
        const preferredId =
          requestedThreadId && nextItems.some((item) => String(item?.id || "") === requestedThreadId)
            ? requestedThreadId
            : selectedIdRef.current;
        const nextSelectedId =
          nextItems.some((item) => String(item?.id || "") === String(preferredId || ""))
            ? preferredId
            : String(nextItems[0]?.id || "");

        setItems(nextItems);
        setTotal(Number(res?.total || 0));
        setSelectedId(nextSelectedId);
      } catch (err) {
        if (!active) return;
        setError(toPortalErrorMessage(err, "Berichten laden mislukt"));
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [limit, page, query, requestedThreadId, statusFilter]);

  useEffect(() => {
    let active = true;

    async function loadDetail() {
      if (!selectedId) {
        setDetail({ thread: null, messages: [] });
        return;
      }

      try {
        setDetailLoading(true);
        const res = await getCustomerPortalCommunicationThread(selectedId);
        if (!active) return;
        setDetail({
          thread: res?.thread || null,
          messages: Array.isArray(res?.messages) ? res.messages : [],
        });
      } catch (err) {
        if (!active) return;
        setError(toPortalErrorMessage(err, "Berichtdetail laden mislukt"));
      } finally {
        if (active) setDetailLoading(false);
      }
    }

    loadDetail();
    return () => {
      active = false;
    };
  }, [selectedId]);

  useEffect(() => {
    if (!requestedThreadId || !selectedId || requestedThreadId === selectedId) return;
    if (items.some((item) => String(item?.id || "") === requestedThreadId)) {
      setSelectedId(requestedThreadId);
    }
  }, [items, requestedThreadId, selectedId]);

  const pagination = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(Math.max(0, total) / limit));
    const safePage = Math.min(Math.max(1, page), totalPages);
    return {
      page: safePage,
      totalPages,
      hasPrevious: safePage > 1,
      hasNext: safePage < totalPages,
    };
  }, [limit, page, total]);

  useEffect(() => {
    if (page > pagination.totalPages) {
      setPage(pagination.totalPages);
    }
  }, [page, pagination.totalPages]);

  async function handleReplySubmit(event) {
    event.preventDefault();
    if (!selectedId || !replyText.trim() || replySaving) return;

    try {
      setReplySaving(true);
      setError("");
      setSuccess("");
      const res = await createCustomerPortalCommunicationReply(selectedId, {
        bodyText: replyText.trim(),
      });

      const nextMessage = res?.message || null;
      const nextThread = res?.thread || detail?.thread || null;

      setDetail((current) => ({
        thread: nextThread,
        messages: nextMessage ? [...(Array.isArray(current.messages) ? current.messages : []), nextMessage] : current.messages,
      }));
      setItems((current) =>
        current.map((item) =>
          String(item?.id || "") === String(selectedId)
            ? { ...item, ...(nextThread || {}), lastMessageAt: nextMessage?.createdAt || item?.lastMessageAt }
            : item
        )
      );
      setReplyText("");
      setSuccess("Reactie geplaatst.");
    } catch (err) {
      setError(toPortalErrorMessage(err, "Reactie plaatsen mislukt"));
    } finally {
      setReplySaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <header className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <nav className="mb-2 text-xs text-slate-500">
            <Link to="/portaal" className="hover:underline">Dashboard</Link>
            <span className="px-1">/</span>
            <span className="font-semibold text-slate-700">Berichten</span>
          </nav>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Klantportaal</p>
              <h1 className="text-2xl font-bold text-slate-900">Berichten</h1>
              <p className="text-sm text-slate-600">Lees klantzichtbare communicatie en reageer direct vanuit het portaal.</p>
            </div>
            <Link
              to="/portaal/aanvragen"
              className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Nieuwe aanvraag
            </Link>
          </div>
        </header>

        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}
        {success ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {success}
          </div>
        ) : null}

        <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              placeholder="Zoek op onderwerp"
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 sm:w-72"
            />
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setPage(1);
                }}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              >
                <option value="">Alle statussen</option>
                <option value="new">Nieuw</option>
                <option value="action_needed">Actie nodig</option>
                <option value="waiting_customer">Wacht op klant</option>
                <option value="waiting_internal">Wacht intern</option>
                <option value="closed">Gesloten</option>
              </select>
              <div className="text-xs text-slate-600">
                Pagina <span className="font-semibold text-slate-800">{pagination.page}</span> van{" "}
                <span className="font-semibold text-slate-800">{pagination.totalPages}</span>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <h2 className="text-sm font-semibold text-slate-900">Gesprekken</h2>
              <span className="text-xs text-slate-500">{total} totaal</span>
            </div>

            {loading ? (
              <p className="px-4 py-6 text-sm text-slate-500">Laden...</p>
            ) : items.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-500">Nog geen berichten beschikbaar voor dit portaal.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {items.map((item) => {
                  const isSelected = String(item?.id || "") === String(selectedId || "");
                  return (
                    <button
                      key={String(item?.id || "")}
                      type="button"
                      onClick={() => {
                        const nextId = String(item?.id || "");
                        setSelectedId(nextId);
                        setSearchParams(nextId ? { thread: nextId } : {});
                      }}
                      className={`block w-full px-4 py-3 text-left transition hover:bg-slate-50 ${
                        isSelected ? "bg-blue-50" : "bg-white"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{item?.subject || "Gesprek"}</p>
                          <p className="mt-1 text-xs text-slate-600">
                            {item?.project?.projectNumber ? `${item.project.projectNumber} • ` : ""}
                            {item?.project?.name || "Algemene communicatie"}
                          </p>
                        </div>
                        <StatusBadge status={item?.status}>{item?.statusLabel}</StatusBadge>
                      </div>
                      <p className="mt-2 text-xs text-slate-500">{formatDateTime(item?.lastMessageAt)}</p>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-xs text-slate-600">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={!pagination.hasPrevious || loading}
                className="rounded-lg border border-slate-300 px-2.5 py-1 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((current) => Math.min(pagination.totalPages, current + 1))}
                disabled={!pagination.hasNext || loading}
                className="rounded-lg border border-slate-300 px-2.5 py-1 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </aside>

          <div className="space-y-4">
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              {detailLoading ? (
                <p className="text-sm text-slate-500">Gesprek laden...</p>
              ) : !detail?.thread ? (
                <p className="text-sm text-slate-500">Selecteer een gesprek om de berichten te bekijken.</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Communicatie</p>
                      <h2 className="text-xl font-bold text-slate-900">{detail.thread.subject || "Gesprek"}</h2>
                      <p className="mt-1 text-sm text-slate-600">
                        {detail.thread.project?.projectNumber ? `${detail.thread.project.projectNumber} • ` : ""}
                        {detail.thread.project?.name || "Algemene klantcommunicatie"}
                      </p>
                    </div>
                    <StatusBadge status={detail?.thread?.status}>
                      {detail?.thread?.statusLabel}
                    </StatusBadge>
                  </div>

                  <div className="grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                    <p>Laatste bericht: <span className="font-medium text-slate-800">{formatDateTime(detail?.thread?.lastMessageAt)}</span></p>
                    <p>Kanaal: <span className="font-medium text-slate-800">{formatChannel(detail?.thread?.channel)}</span></p>
                  </div>
                </div>
              )}
            </section>

            <section className="space-y-3">
              {detailLoading ? null : Array.isArray(detail?.messages) && detail.messages.length > 0 ? (
                detail.messages.map((message) => (
                  <article key={String(message?.id || "")} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500">{formatDirection(message?.direction)}</p>
                        <p className="mt-1 text-sm font-medium text-slate-900">
                          {message?.from?.name || message?.from?.email || "Onbekende afzender"}
                        </p>
                      </div>
                      <p className="text-xs text-slate-500">{formatDateTime(message?.createdAt)}</p>
                    </div>
                    {message?.subject ? (
                      <p className="mt-3 text-sm font-medium text-slate-800">{message.subject}</p>
                    ) : null}
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                      {message?.bodyText || stripHtml(message?.bodyHtml) || "Geen inhoud."}
                    </p>
                  </article>
                ))
              ) : !detailLoading && detail?.thread ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
                  Nog geen klantzichtbare berichten in dit gesprek.
                </div>
              ) : null}
            </section>

            <form onSubmit={handleReplySubmit} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-900">Reageer op dit gesprek</h3>
              <p className="mt-1 text-xs text-slate-500">
                Alleen klantzichtbare communicatie wordt hier getoond en opgeslagen.
              </p>
              {detail?.thread?.portalAllowReply === false ? (
                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-600">
                  Reageren is voor dit gesprek uitgeschakeld. Neem contact op met uw contactpersoon als er opvolging nodig is.
                </div>
              ) : (
                <>
                  <textarea
                    value={replyText}
                    onChange={(event) => setReplyText(event.target.value)}
                    rows={5}
                    placeholder="Typ hier uw reactie"
                    className="mt-3 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    disabled={!detail?.thread || replySaving}
                  />
                  <div className="mt-3 flex justify-end">
                    <button
                      type="submit"
                      disabled={!detail?.thread || !replyText.trim() || replySaving}
                      className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {replySaving ? "Versturen..." : "Reactie versturen"}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("nl-NL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function stripHtml(value) {
  return String(value || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function formatDirection(direction) {
  if (direction === "incoming") return "Inkomend";
  if (direction === "outgoing") return "Uitgaand";
  return "Bericht";
}

function formatChannel(channel) {
  const key = String(channel || "").trim().toLowerCase();
  if (key === "portal" || key === "portal_message") return "Portaal";
  if (key === "email") return "E-mail";
  return "Handmatig";
}

function StatusBadge({ status, children }) {
  const key = String(status || "").trim().toLowerCase();
  const className =
    key === "closed"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : key === "waiting_customer"
        ? "border-violet-200 bg-violet-50 text-violet-700"
        : key === "waiting_internal"
          ? "border-slate-200 bg-slate-100 text-slate-700"
          : key === "action_needed"
            ? "border-amber-200 bg-amber-50 text-amber-800"
            : "border-blue-200 bg-blue-50 text-blue-700";

  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${className}`}>
      {children || key || "Nieuw"}
    </span>
  );
}
